import { useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconCode } from '@calendly/ui/components/glyphs/IconCode';
import { IconCopy } from '@calendly/ui/components/glyphs/IconCopy';
import { IconDotsVertical } from '@calendly/ui/components/glyphs/IconDotsVertical';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { IconEye } from '@calendly/ui/components/glyphs/IconEye';
import { IconEyeOff } from '@calendly/ui/components/glyphs/IconEyeOff';
import { IconNote } from '@calendly/ui/components/glyphs/IconNote';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { IconShieldLock } from '@calendly/ui/components/glyphs/IconShieldLock';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import { BodyText } from '@calendly/ui/components/typography';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextDisabled, colorTextStandard } from '@calendly/ui/theme';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { InternalNotePopup } from 'components/event_type_edit';
import { useActivateEventTypeMutation, useCloneEventTypeMutation, useDeactivateEventTypeMutation, useDeleteEventTypeMutation, useSaveEventTypeInternalNoteMutation, useUpdateEventTypeMutation } from 'features/event_type_editor';
import { EditPermissionsModal, isObjectLevelPermissionsAvailableSelector, openEditPermissionsModal } from 'features/object_level_permissions';
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path';
import { DeleteEventTypeConfirmationPopup } from './delete_event_type_confirmation_popup';
import { EventColorMenu } from './event_color_menu';
import { PageLanguageMenu } from './page_language_menu';
import { UpdatePageLanguageConfirmationPopup } from './update_page_language_confirmation_popup';

// SwitchField's own <label> row is width: fit-content and can't be restyled from outside (no
// className hook, and styling it via a nested `label {}` selector isn't allowed - see
// local/no-bare-selectors). Composing SwitchInput + BodyText directly under our own <label>
// gives the same label-toggles-switch behavior with a row we fully control (flush-right layout).
const onOffRowCss = "o4iae9q";

// Self-contained, same pattern as CopyLinkControls in editor_header.js: owns its own isOpen
// state and all the popups/mutations its menu items need.
export const HeaderMoreMenu = ({
  eventType,
  isPhone = false
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isInternalNotePopupOpen, setIsInternalNotePopupOpen] = useState(false);
  const [pendingLocale, setPendingLocale] = useState(null);
  // Event color is a draft form value, matching the legacy editor's ColorPicker: the editor form
  // owns `color` and sends it with every save, so writing it here through its own PATCH would be
  // overwritten by the next save. Selecting a color marks the form dirty instead.
  const [{
    value: colorValue
  },, {
    setValue: setColorValue
  }] = useFormField({
    name: 'color'
  });
  // Page language is a draft form value too (see merge_initial_values.js/root_form.js): after
  // confirmation, a picked locale is staged here instead of firing its own PATCH, so it rides
  // along with the next "Save changes" like every other pending edit.
  const [{
    value: localeValue
  },, {
    setValue: setLocaleValue
  }] = useFormField({
    name: 'locale'
  });
  // EventColorMenu and PageLanguageMenu each own an independent DropdownMenu (see
  // event_color_menu.js/page_language_menu.js) that would otherwise portal to document.body
  // separately from this kebab's menu. Sharing one portal container keeps a click in either
  // flyout inside this menu's own tracked DOM, instead of reading as an outside click that closes
  // the kebab before the selection commits.
  const [menuPortalRoot, setMenuPortalRoot] = useState(null);
  useEffect(() => {
    const portalNode = document.createElement('div');
    document.body.appendChild(portalNode);
    setMenuPortalRoot(portalNode);
    return () => {
      document.body.removeChild(portalNode);
    };
  }, []);
  const isObjectLevelPermissionsAvailable = useSelector(isObjectLevelPermissionsAvailableSelector);
  // "Large desktop" per design (>1500px): defaultBreakpointQueries has no single query for this,
  // so combine isDesktopUp (>1100px) with the isLargeDesktopDown band it overlaps (1101-1500px).
  const isDesktopUp = useDeviceType('isDesktopUp');
  const isLargeDesktopDown = useDeviceType('isLargeDesktopDown');
  const isLargeDesktop = isDesktopUp && !isLargeDesktopDown;

  // Scoped fixedCacheKey: keeps this menu's toggles off EditorHeader's
  // `update-event-type-${id}` instance, whose `error` drives the Publish/Save
  // hasMutationError check — a failed toggle here shouldn't look like a failed save.
  const [updateEventType, {
    isLoading: isUpdatingEventType
  }] = useUpdateEventTypeMutation({
    id: eventType.id,
    fixedCacheKey: `header-more-menu-update-event-type-${eventType.id}`
  });
  const [saveInternalNote] = useSaveEventTypeInternalNoteMutation();
  const [activate, {
    isLoading: isActivating
  }] = useActivateEventTypeMutation();
  const [deactivate, {
    isLoading: isDeactivating
  }] = useDeactivateEventTypeMutation();
  const [cloneEventType, {
    isLoading: isCloning
  }] = useCloneEventTypeMutation();
  const [deleteEventType, {
    isLoading: isDeleting
  }] = useDeleteEventTypeMutation();
  const isTeam = eventType.owner_type === 'Page';
  const meetingTypesPath = useMeetingTypesPath(`${isTeam ? 'team' : 'user'}/${eventType.owner_id}`);
  const canEditPermissions = isObjectLevelPermissionsAvailable && !isTeam;
  const availableLocales = eventType.available_locales || [];
  const handleToggleVisibility = () => updateEventType({
    eventType,
    attributes: {
      public: !eventType.public
    },
    successMessage: I18n.t('publisher.event_type_editor.header.settings.visibility.changed')
  });
  const handleSaveInternalNote = internalNote => saveInternalNote({
    eventType,
    internalNote
  });
  const activeColor = colorValue ?? eventType.color;
  const activeLocale = localeValue ?? eventType.locale;
  const handleSelectLocale = identifier => {
    if (identifier === activeLocale) return;
    setPendingLocale(identifier);
  };
  const handleConfirmLocale = () => {
    if (pendingLocale != null) {
      setLocaleValue(pendingLocale);
    }
    setPendingLocale(null);
  };
  const handleCancelLocale = () => {
    setPendingLocale(null);
  };
  const handleToggleActive = checked => checked ? activate({
    eventType
  }) : deactivate({
    eventType
  });
  const handleEditPermissions = () => dispatch(openEditPermissionsModal(eventType));
  const handleClone = () => cloneEventType({
    ownerId: eventType.owner_id,
    ownerType: eventType.owner_type,
    eventTypeId: eventType.id,
    onSuccess: ({
      id
    }) => history.push(`/event_types/${id}/editor`)
  });
  const handleDelete = async () => {
    const result = await deleteEventType({
      ownerId: eventType.owner_id,
      ownerType: eventType.owner_type,
      eventTypeId: eventType.id,
      uuid: eventType.uuid
    });
    if (!result?.error) {
      history.push(meetingTypesPath);
    }
  };
  return <>
      <DropdownMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} menuPortalRoot={menuPortalRoot}>
        <DropdownMenuTrigger>
          <IconButton
        // Bordered next to Copy link/Publish on tablet+; borderless once those are hidden on phone.
        appearance={isPhone ? 'ghost' : 'secondary-outline'} size="medium" icon={IconDotsVertical} aria-label={I18n.t('publisher.event_type_editor_crud.header.more_options')} onClick={() => setIsMenuOpen(isOpen => !isOpen)} data-testid="editor-header-kebab" />
        </DropdownMenuTrigger>
        <DropdownMenuContent placement="bottom end" menuStyle={!isPhone ? {
        width: '268px'
      } : undefined}>
          <DropdownMenuGroup>
            {/* Per design, "View live page" is omitted from the kebab at the large-desktop
                breakpoint (>1500px); it shows at every other size. */}
            {!isLargeDesktop && <DropdownMenuItem href={eventType.booking_url} target="_blank" rel="noopener noreferrer" data-testid="header-more-menu-view-live-page">
                <UiIcon icon={IconExternalLink} size="small" title="" />
                {I18n.t('publisher.event_types.view_live_page')}
              </DropdownMenuItem>}
            {/* PageLanguageMenu is its own independent DropdownMenu, not a DropdownSubmenu - see
                page_language_menu.js for why. disableAutoClose stops interacting inside this row
                from triggering the kebab's own onAction/close; `onLocaleSelected` closes the
                kebab once a locale is actually picked. */}
            <DropdownMenuItem disableAutoClose data-testid="header-more-menu-page-language">
              <PageLanguageMenu activeLocale={activeLocale} availableLocales={availableLocales} onSelectLocale={handleSelectLocale} onLocaleSelected={() => setIsMenuOpen(false)} menuPortalRoot={menuPortalRoot} />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setIsInternalNotePopupOpen(true)} data-testid="header-more-menu-internal-note">
              <UiIcon icon={IconNote} size="small" title="" />
              {eventType.internal_note ? I18n.t('publisher.event_types.internal_note.edit') : I18n.t('publisher.event_types.internal_note.add')}
            </DropdownMenuItem>
            {/* TODO(cui-drift): Add to website needs profile-level branding data (see
                features/scheduling/.../dropdown.js's useCurrentProfile()) that this standalone
                editor route doesn't fetch. Ship disabled until that data is available here. */}
            <DropdownMenuItem disabled data-testid="header-more-menu-add-to-website">
              <UiIcon icon={IconCode} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.kebab.add_to_website')}
            </DropdownMenuItem>
            {canEditPermissions && <DropdownMenuItem onSelect={handleEditPermissions} data-testid="header-more-menu-edit-permissions">
                <UiIcon icon={IconShieldLock} size="small" title="" />
                {I18n.t('publisher.event_types.edit_permissions')}
              </DropdownMenuItem>}
            <DropdownMenuItem onSelect={handleToggleVisibility} disabled={isUpdatingEventType} data-testid="header-more-menu-toggle-visibility">
              <UiIcon icon={eventType.public ? IconEyeOff : IconEye} size="small" title="" />
              {eventType.public ? I18n.t('publisher.event_type_editor_crud.header.kebab.hide_on_landing_page') : I18n.t('publisher.event_type_editor_crud.header.kebab.show_on_landing_page')}
            </DropdownMenuItem>
            {/* EventColorMenu is its own independent DropdownMenu, not a DropdownSubmenu - see
                event_color_menu.js for why. disableAutoClose stops interacting inside this row
                from triggering the kebab's own onAction/close (same technique as the on/off
                switch row below); `onColorSelected` closes the kebab once a color is committed. */}
            <DropdownMenuItem disableAutoClose data-testid="header-more-menu-event-color">
              <EventColorMenu activeColor={activeColor} onSelectColor={setColorValue} onColorSelected={() => setIsMenuOpen(false)} menuPortalRoot={menuPortalRoot} />
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem disabled data-testid="header-more-menu-rename">
              <UiIcon icon={IconPencil} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.kebab.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleClone} disabled={isCloning} data-testid="header-more-menu-duplicate">
              <UiIcon icon={IconCopy} size="small" title="" />
              {I18n.t('publisher.action.duplicate')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setIsDeletePopupOpen(true)} disabled={isDeleting} data-testid="header-more-menu-delete">
              <UiIcon icon={IconTrash} size="small" title="" />
              {I18n.t('publisher.action.delete')}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem disableAutoClose data-testid="header-more-menu-on-off">
              <label className={onOffRowCss}>
                <BodyText size="medium" color={isActivating || isDeactivating ? colorTextDisabled : colorTextStandard} inline>
                  {I18n.t('publisher.event_types.context.on_off')}
                </BodyText>
                <SwitchInput checked={eventType.active} disabled={isActivating || isDeactivating} onChange={event => handleToggleActive(event.currentTarget.checked)} />
              </label>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteEventTypeConfirmationPopup isOpen={isDeletePopupOpen} name={eventType.name} isDeleting={isDeleting} onConfirm={handleDelete} onClose={() => setIsDeletePopupOpen(false)} />
      <InternalNotePopup isOpen={isInternalNotePopupOpen} internalNote={eventType.internal_note} onConfirm={handleSaveInternalNote} onClose={() => setIsInternalNotePopupOpen(false)} />
      <UpdatePageLanguageConfirmationPopup isOpen={pendingLocale != null} onClose={handleCancelLocale} onConfirm={handleConfirmLocale} />
      <EditPermissionsModal />
    </>;
};

require("./header_more_menu.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./header_more_menu.js");